/**
 * Authentication Utilities
 *
 * Centralized utilities for generating authentication URLs consistently
 * across the frontend application.
 */

import { buildApiHostname, getEnvironmentInfo } from "./subdomainUtils";

// ---------- Helpers ----------

function getBaseDomain(hostname: string): string {
  const parts = hostname.split(".");
  return parts.slice(-2).join(".");
}

function withProtocol(domain: string, prefix: string, region?: string): string {
  const protocol = domain.includes("localhost") ? "http" : "https";

  // Add region suffix if provided and not US
  let fullPrefix = prefix;
  if (region && region !== "us") {
    fullPrefix = `${prefix}-${region}`;
  }

  return `${protocol}://${fullPrefix}.${domain}`;
}

// ---------- Core Utilities ----------

export function getAuthHostForDomain(currentDomain: string): string {
  return withProtocol(currentDomain, "accounts");
}

export function getAuthHost(): string {
  if (typeof window !== "undefined") {
    return getAuthHostForDomain(getBaseDomain(window.location.hostname));
  }
  return withProtocol("highbond.com", "accounts");
}

export function getAccountsLoginUrl(domain: string): string {
  return `${getAuthHostForDomain(domain)}/login`;
}

export function generateAuthServiceUrl(domain: string): string {
  // Get region information for URL construction
  const envInfo = getEnvironmentInfo();

  return `${withProtocol(domain, "authy-api", envInfo.region)}/session`;
}

export function generateAuthServiceUrlWithLogging(
  domain: string,
  environment: string
): string {
  const url = generateAuthServiceUrl(domain);

  if (environment !== "localhost") {
    console.log(`🔐 Auth Service: Domain='${domain}', URL='${url}'`);
  }

  return url;
}

export function generateApiUrl(): string {
  if (typeof window !== "undefined") {
    const parts = window.location.hostname.split(".");
    if (parts.length >= 3) {
      const [client, , ...rest] = parts;
      const domain = rest.join(".");
      const protocol = domain.includes("localhost") ? "http" : "https";
      const port = protocol === "http" ? ":3001" : "";

      // Use buildApiHostname for consistent region handling
      const apiHostname = buildApiHostname(client, domain);

      return `${protocol}://${apiHostname}${port}/api/v1`;
    }
  }
  return "http://localhost:3001/api/v1";
}

export function getAuthUrl(path: string): string {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${getAuthHost()}${cleanPath}`;
}

// ---------- Predefined URLs ----------

export const AUTH_URLS = {
  login: () => getAuthUrl("/login"),
  logout: () => getAuthUrl("/logout"),
  profile: () => getAuthUrl("/profile"),
  tokenRefresh: () => getAuthUrl("/api/token/refresh"),
} as const;

// ---------- JWT Config ----------

export function getJwtClientConfig() {
  return {
    tokenRefreshUrl: AUTH_URLS.tokenRefresh(),
    autoRedirect: import.meta.env.VITE_AUTO_REDIRECT === "true",
    loginUrl: AUTH_URLS.login(),
    noSubdomain: import.meta.env.VITE_NO_SUBDOMAIN === "true",
  };
}
